import { useI18nContext } from '@/i18n/i18n-react';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import type { StoreCampaignAverages } from '@/hooks/use-store-campaign-averages';
import { compareToAverage } from '@/lib/notification-funnel';
import { formatCurrency } from '@/components/metrics/formatters';
import {
  NotificationFunnel,
  type FunnelStat,
} from '@/components/funnel/notification-funnel';
import { Skeleton } from '@/components/ui/skeleton';

export interface CampaignResultsSectionProps {
  analytics: CampaignDto['analytics'];
  /** Store averages of the last 90 days; `null` = no comparison labels. */
  averages: StoreCampaignAverages | null;
  /** `undefined` while the store currency loads: Receita is a skeleton. */
  currency: string | undefined;
  /** Phone (CAMP-05): compact funnel, list below. */
  compact: boolean;
}

const DASH = '—';

/**
 * "Resultados" of a sent campaign (CAMP-01…03): the notification funnel of
 * the campaign and, under "Comparado à média das suas campanhas", Viraram
 * pedido, CTR and Receita, each with "acima/abaixo da média · {média}" when
 * the store has 2+ campaigns in the window. `shouldHideData` → every value
 * "—" and no labels.
 */
export function CampaignResultsSection({
  analytics,
  averages,
  currency,
  compact,
}: CampaignResultsSectionProps) {
  const { LL, locale } = useI18nContext();
  const hide = analytics.dataSufficiency.shouldHideData;
  const campaignsInWindow = averages?.campaignsCount ?? 0;

  const percent = (ratio: number) =>
    new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(ratio);
  const money = (value: number, moneyCurrency: string) =>
    formatCurrency(value, locale, moneyCurrency);

  /** The comparison label of `value` against `average`, if any. */
  const comparison = (
    value: number | null,
    average: number | null,
    formatAverage: (average: number) => string
  ): FunnelStat['comparison'] => {
    const { kind } = compareToAverage(
      hide ? null : value,
      average,
      campaignsInWindow
    );
    return kind === 'none' || average === null
      ? undefined
      : { kind, average: formatAverage(average) };
  };

  const conversion =
    analytics.notificationsSent === 0
      ? null
      : analytics.paidOrderCount / analytics.notificationsSent;

  const revenueValue = hide ? (
    DASH
  ) : currency === undefined ? (
    <Skeleton data-testid="currency-skeleton" className="h-5 w-24" />
  ) : (
    money(analytics.revenue, currency)
  );

  const stats: FunnelStat[] = [
    {
      label: LL.campaignDetails.results.converted(),
      hint: LL.campaignDetails.results.convertedHint(),
      value: hide || conversion === null ? DASH : percent(conversion),
      comparison: comparison(conversion, averages?.conversion ?? null, percent),
    },
    {
      label: LL.campaignDetails.results.ctr(),
      hint: LL.campaignDetails.results.ctrHint(),
      value: hide ? DASH : percent(analytics.ctr),
      comparison: comparison(analytics.ctr, averages?.ctr ?? null, percent),
    },
    {
      label: LL.campaignDetails.results.revenue(),
      hint: LL.campaignDetails.results.revenueHint(),
      value: revenueValue,
      // The average needs the currency too: no label until it loads.
      comparison:
        currency === undefined
          ? undefined
          : comparison(analytics.revenue, averages?.revenue ?? null, (avg) =>
              money(avg, currency)
            ),
    },
  ];

  return (
    <NotificationFunnel
      compact={compact}
      hideValues={hide}
      stages={{
        sent: analytics.notificationsSent,
        delivered: analytics.notificationsSuccessfullyDelivered,
        clicks: analytics.clickCount,
        orders: analytics.paidOrderCount,
      }}
      statsTitle={LL.campaignDetails.results.statsTitle()}
      stats={stats}
    />
  );
}
